import { useQuery } from '@tanstack/react-query';

import { REACT_APP_API_FETCH_INTERVAL } from '#shared';

import { weatherApi } from '../aviationWeather.api';

import type { AirMetar } from '#entities';

const transform = (raw: string) => {
  try {
    const rows = raw.split('\n');
    if (rows.length < 6) {
      throw new Error('Corrupted CSV file');
    }
    const rawData = rows.slice(6);

    const data: AirMetar[] = rawData
      .filter(i => !!i)
      .map(row => {
        const [
          rawData,
          stationId,
          observationTime,
          latitude,
          longitude,
          tempC,
          dewpointC,
          windDirDegrees,
          windSpeedKt,
          windGustKt,
          visibilityStatuteMi,
          altimInHg,
          seaLevelPressureMb,
          corrected,
          auto,
          autoStation,
          maintenanceIndicatorOn,
          noSignal,
          lightningSensorOff,
          freezingRainSensorOff,
          presentWeatherSensorOff,
          wxString,
          skyCover0,
          cloudBaseFtAgl0,
          skyCover1,
          cloudBaseFtAgl1,
          skyCover2,
          cloudBaseFtAgl2,
          skyCover3,
          cloudBaseFtAgl3,
          flightCategory,
          threeHrPressureTendencyMb,
          maxTC,
          minTC,
          maxT24hrC,
          minT24hrC,
          precipIn,
          pcp3hrIn,
          pcp6hrIn,
          pcp24hrIn,
          snowIn,
          vertVisFt,
          metarType,
          elevationM,
        ] = row.split(',');

        const metar: AirMetar = {
          stationId,
          observationTime,
          latitude: parseFloat(latitude),
          longitude: parseFloat(longitude),
          tempC: parseFloat(tempC),
          dewpointC: parseFloat(dewpointC),
          windDirDegrees:
            windDirDegrees === 'VRB' ? 'VRB' : parseInt(windDirDegrees),
          windSpeedKt: parseInt(windSpeedKt),
          windGustKt: parseInt(windGustKt),
          visibilityStatuteMi: parseFloat(visibilityStatuteMi),
          altimInHg: parseFloat(altimInHg),
          seaLevelPressureMb: parseInt(seaLevelPressureMb),
          corrected,
          wxString,
          qualityControlFlags: {
            auto: auto === 'TRUE',
            autoStation: autoStation === 'TRUE',
            presentWeatherSensorOff: presentWeatherSensorOff === 'TRUE',
            maintenanceIndicatorOn: maintenanceIndicatorOn === 'TRUE',
            lightningSensorOff: lightningSensorOff === 'TRUE',
            noSignal: noSignal === 'TRUE',
            freezingRainSensorOff: freezingRainSensorOff === 'TRUE',
            corrected: corrected === 'TRUE',
          },
          skyCondition: [
            {
              skyCover: skyCover0,
              cloudBaseFtAgl: cloudBaseFtAgl0,
            },
            {
              skyCover: skyCover1,
              cloudBaseFtAgl: cloudBaseFtAgl1,
            },
            {
              skyCover: skyCover2,
              cloudBaseFtAgl: cloudBaseFtAgl2,
            },
            {
              skyCover: skyCover3,
              cloudBaseFtAgl: cloudBaseFtAgl3,
            },
          ],
          flightCategory: flightCategory as AirMetar['flightCategory'],
          threeHrPressureTendencyMb,
          maxTC,
          minTC: parseInt(minTC),
          maxT24hrC,
          minT24hrC,
          pcp3hrIn,
          pcp6hrIn,
          pcp24hrIn,
          snowIn,
          metarType: metarType as AirMetar['metarType'],
          elevationM: elevationM ? parseInt(elevationM) : undefined,
          precipIn: precipIn ? parseInt(precipIn) : null,
          vertVisFt: vertVisFt ? parseInt(vertVisFt) : null,
          rawData: rawData.replace(/^"|"$/g, ''),
        };
        return metar;
      });
    return data;
  } catch (error) {
    console.error('Error transforming metar data:', error);
    return [];
  }
};

export const useQueryMetars = () =>
  useQuery({
    queryKey: ['weather', 'metars'],
    queryFn: weatherApi.getMetars,
    select: transform,
    refetchInterval: 1000 * REACT_APP_API_FETCH_INTERVAL,
    refetchIntervalInBackground: true,
    placeholderData: previousData => previousData,
  });
